import { hc } from "hono/client";
import type { AppType } from "@salon/api";
import { useAuth } from "../stores/auth";

// API base URL — relative "/" mặc định:
//   - Dev: Vite proxy /api/* → wrangler:8787 (xem vite.config.ts)
//   - Prod: Pages Function /api/* → Worker (xem functions/api/[[path]].ts)
// → 1 codebase, không cần .env riêng cho từng môi trường.
// Vẫn cho phép override qua VITE_API_BASE nếu cần (vd debug, point sang remote API).
const API_BASE = (import.meta.env.VITE_API_BASE || "/").replace(/\/$/, "") || "/";
const apiUrl = (path: string) =>
  API_BASE === "/" ? path : `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

// ─── Auth flow ────────────────────────────────────────────────────────
// Token nằm trong Authorization Bearer header (không dùng cookie để tránh
// iOS Safari ITP block third-party cookie). Token persist qua zustand
// (localStorage). Refresh trước khi token hết hạn 30s.

const SKEW_MS = 30_000;

function isAuthEndpoint(url: string): boolean {
  return (
    url.includes("/api/auth/login") ||
    url.includes("/api/auth/refresh") ||
    url.includes("/api/auth/logout")
  );
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

// ─── Refresh logic ────────────────────────────────────────────────────
// Single-flight: nhiều request concurrent share 1 lần refresh.

let refreshInFlight: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const { refreshToken } = useAuth.getState();
  if (!refreshToken) return false;
  try {
    const res = await fetch(apiUrl("/api/auth/refresh"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) {
      // Refresh fail → token đã revoke / hết hạn → clear auth state.
      useAuth.getState().clear();
      return false;
    }
    const data = (await res.json().catch(() => null)) as
      | {
          accessToken?: string;
          refreshToken?: string;
          accessExpiresAt?: number;
          refreshExpiresAt?: number;
        }
      | null;
    if (
      data?.accessToken &&
      data.refreshToken &&
      data.accessExpiresAt &&
      data.refreshExpiresAt
    ) {
      useAuth.getState().setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        accessExpiresAt: data.accessExpiresAt,
        refreshExpiresAt: data.refreshExpiresAt,
      });
      return true;
    }
    useAuth.getState().clear();
    return false;
  } catch {
    useAuth.getState().clear();
    return false;
  }
}

function scheduleRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      // reset sau 1 tick để các waiter concurrent cùng nhận kết quả này
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    });
  }
  return refreshInFlight;
}

function attachAuthHeader(init: RequestInit | undefined): RequestInit {
  const { accessToken } = useAuth.getState();
  if (!accessToken) return init ?? {};
  const headers = new Headers(init?.headers);
  if (!headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  return { ...init, headers };
}

async function fetchWithRefresh(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const url = urlOf(input);
  const { accessExpiresAt, accessToken } = useAuth.getState();

  // Proactive: nếu access sắp/đã hết hạn, refresh trước khi gửi request.
  // Bỏ qua nhánh này cho chính các endpoint auth để tránh loop.
  if (
    !isAuthEndpoint(url) &&
    accessToken &&
    accessExpiresAt !== null &&
    Date.now() >= accessExpiresAt - SKEW_MS
  ) {
    await scheduleRefresh();
  }

  const res = await fetch(input, attachAuthHeader(init));
  if (res.status !== 401) return res;
  if (isAuthEndpoint(url)) return res;

  // Reactive: 401 không lường trước (token bị revoke giữa chừng, clock skew lớn, v.v.).
  const ok = await scheduleRefresh();
  if (!ok) {
    useAuth.getState().clear();
    return res;
  }
  return fetch(input, attachAuthHeader(init));
}

// Hono RPC client — type-safe end-to-end. Base URL từ VITE_API_BASE (cross-origin
// prod) hoặc "/" (dev: Vite proxy sang wrangler:8787; same-origin prod Cách A).
export const api = hc<AppType>(API_BASE, {
  fetch: fetchWithRefresh,
});

export type ApiClient = typeof api;

// Helper: ném lỗi với message đẹp khi response không OK.
// Type lỏng để dùng được với cả native Response và Hono ClientResponse.
type UnwrapResponse = {
  ok: boolean;
  status: number;
  json(): Promise<any>;
};

// Đọc message lỗi từ response không OK. Match shape error của API
// (`{error, message, details}` từ apps/api/src/lib/errors.ts) + fallback HTTP status.
export async function readErrorMessage(res: UnwrapResponse): Promise<string> {
  const detail = (await res.json().catch(() => null)) as
    | { error?: string; message?: string }
    | null;
  return detail?.message || detail?.error || `HTTP ${res.status}`;
}

export async function unwrap<T = any>(p: Promise<UnwrapResponse>): Promise<T> {
  const res = await p;
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return (await res.json()) as T;
}
